import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { motion } from "framer-motion";
import { Clock, Trophy, ChevronDown, ChevronUp, CheckCircle2, XCircle, Star, Waves } from "lucide-react";

export default function MyBets() {
  const [items, setItems] = useState([]);
  const [yoles, setYoles] = useState([]);
  const [expanded, setExpanded] = useState({}); // {rankingId: true}
  const { user } = useAuth();

  useEffect(() => {
    (async () => {
      const [r, y] = await Promise.all([api.get("/rankings/me"), api.get("/yoles")]);
      setItems(r.data);
      setYoles(y.data);
    })();
  }, []);

  const yoleById = useMemo(() => Object.fromEntries(yoles.map((y) => [y.id, y])), [yoles]);
  const pending = items.filter((r) => r.status === "pending");
  const settled = items.filter((r) => r.status === "settled");

  return (
    <div className="space-y-6" data-testid="my-bets-page">
      <div>
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-[color:var(--deep)]">
          Mes pronostics
        </h1>
        <p className="text-sm text-slate-500 mt-2">
          Le détail de chaque pari : points gagnés, pronostics réussis et ratés.
        </p>
      </div>

      <Section
        title="En attente"
        items={pending}
        icon={Clock}
        tint="bg-[color:var(--ocean)]/10 text-[color:var(--ocean)]"
        empty="Aucun pronostic en cours. Choisis une étape à venir."
        yoleById={yoleById}
        expanded={expanded}
        setExpanded={setExpanded}
        favoriteYoleId={user?.favorite_yole_id}
      />
      <Section
        title="Historique"
        items={settled}
        icon={Trophy}
        tint="bg-slate-100 text-slate-600"
        empty="Pas encore de pronostics réglés."
        yoleById={yoleById}
        expanded={expanded}
        setExpanded={setExpanded}
        favoriteYoleId={user?.favorite_yole_id}
      />
    </div>
  );
}

function Section({ title, items, icon: Icon, tint, empty, yoleById, expanded, setExpanded, favoriteYoleId }) {
  return (
    <section>
      <div className="flex items-center gap-2 mb-3">
        <div className={`inline-flex items-center justify-center w-7 h-7 rounded-lg ${tint}`}>
          <Icon className="h-4 w-4" strokeWidth={2.4} />
        </div>
        <h2 className="font-display text-lg font-extrabold text-[color:var(--deep)]">{title}</h2>
      </div>
      {items.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-8 text-center text-sm text-slate-500">
          {empty}
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl divide-y divide-slate-100">
          {items.map((r, i) => (
            <motion.div
              key={r.id}
              initial={{ opacity: 0, x: -6 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.03 }}
              className="p-4"
              data-testid={`my-ranking-${r.id}`}
            >
              <RankingRow
                r={r}
                yoleById={yoleById}
                open={!!expanded[r.id]}
                onToggle={() => setExpanded((s) => ({ ...s, [r.id]: !s[r.id] }))}
                favoriteYoleId={favoriteYoleId}
              />
            </motion.div>
          ))}
        </div>
      )}
    </section>
  );
}

function RankingRow({ r, yoleById, open, onToggle, favoriteYoleId }) {
  const isSettled = r.status === "settled";
  const finalRank = r.stage_final_ranking || [];
  const capsized = new Set(r.stage_capsized_yole_ids || []);

  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        data-testid={`my-ranking-toggle-${r.id}`}
        className="w-full text-left flex items-baseline justify-between gap-2 group"
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2">
            <div className="font-bold text-[color:var(--deep)] group-hover:text-[color:var(--coral)] truncate">
              {r.stage_name || "Étape"}
            </div>
            <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform flex-shrink-0 ${open ? "rotate-180" : ""}`} />
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            {isSettled
              ? `Classement +${r.points_ranking} · Dessalage +${r.points_capsize} · Favorite +${r.points_favorite}`
              : `${r.ranking.length} yoles classées · ${r.capsize_predictions.length} dessalages prédits`}
          </div>
        </div>
        <div className="flex-shrink-0">
          {isSettled ? (
            <div
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-black ${
                r.total_points > 0
                  ? "bg-[color:var(--palm)]/20 text-[color:var(--palm)]"
                  : "bg-slate-100 text-slate-500"
              }`}
            >
              <Trophy className="h-3 w-3" /> {r.total_points > 0 ? `+${r.total_points}` : "0"}
            </div>
          ) : (
            <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[color:var(--ocean)]/15 text-[color:var(--ocean)] text-[11px] font-black">
              <Clock className="h-3 w-3" /> en cours
            </div>
          )}
        </div>
      </button>

      {open && (
        <div className="mt-4 space-y-4" data-testid={`my-ranking-detail-${r.id}`}>
          {/* Classement */}
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5">
              Ton classement
            </div>
            <div className="space-y-1">
              {r.ranking.map((yid, idx) => {
                const y = yoleById[yid];
                const pickedPos = idx + 1; // 1-based
                const actualIdx = finalRank.indexOf(yid);
                const actualPos = actualIdx >= 0 ? actualIdx + 1 : null;
                const scored = isSettled && actualIdx === idx; // same position → scored
                const yoleName = y?.name || `Yole ${yid.slice(0, 6)}`;
                return (
                  <div
                    key={yid}
                    className={`flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-xs ${
                      isSettled
                        ? scored
                          ? "bg-emerald-50 border border-emerald-200"
                          : "bg-slate-50 border border-slate-100"
                        : "bg-slate-50"
                    }`}
                  >
                    <span className="font-mono font-bold text-[color:var(--deep)] w-6">
                      {pickedPos}.
                    </span>
                    {y ? (
                      <div
                        className="w-3 h-3 rounded-full flex-shrink-0"
                        style={{ backgroundColor: y.color }}
                      />
                    ) : null}
                    <span className="font-bold truncate flex-1">{yoleName}</span>
                    {isSettled && (
                      <span className="flex items-center gap-1 flex-shrink-0">
                        {scored ? (
                          <>
                            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                            <span className="text-[10px] font-mono font-bold text-emerald-700">
                              +{r.ranking.length - idx}
                            </span>
                          </>
                        ) : actualPos ? (
                          <span className="text-[10px] font-mono text-slate-400">
                            (fini {actualPos}
                            <sup>{actualPos === 1 ? "re" : "e"}</sup>)
                          </span>
                        ) : (
                          <span className="text-[10px] font-mono text-slate-400">(hors top)</span>
                        )}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Dessalages */}
          {r.capsize_predictions.length > 0 && (
            <div>
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5 flex items-center gap-1.5">
                <Waves className="h-3 w-3" /> Dessalages prédits
              </div>
              <div className="flex flex-wrap gap-1.5">
                {r.capsize_predictions.map((yid) => {
                  const y = yoleById[yid];
                  const won = isSettled && capsized.has(yid);
                  const missed = isSettled && !capsized.has(yid);
                  return (
                    <div
                      key={yid}
                      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold border ${
                        won
                          ? "bg-emerald-50 border-emerald-300 text-emerald-800"
                          : missed
                          ? "bg-slate-50 border-slate-200 text-slate-400 line-through"
                          : "bg-slate-50 border-slate-200 text-slate-600"
                      }`}
                    >
                      {y ? (
                        <div
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: y.color }}
                        />
                      ) : null}
                      <span>{y?.name || yid.slice(0, 6)}</span>
                      {won && <CheckCircle2 className="h-3 w-3 text-emerald-600" />}
                      {missed && <XCircle className="h-3 w-3 text-slate-400" />}
                    </div>
                  );
                })}
              </div>
              {isSettled && (
                <div className="text-[10px] text-slate-500 mt-1.5">
                  {r.points_capsize > 0
                    ? `Total dessalage : +${r.points_capsize} pts`
                    : "Aucun dessalage réussi sur cette étape."}
                </div>
              )}
            </div>
          )}

          {/* Yole favorite */}
          {favoriteYoleId && isSettled && (
            <div className="flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-xs bg-[color:var(--sunshine)]/10 border border-[color:var(--sunshine)]/30">
              <Star className="h-3.5 w-3.5 text-[color:var(--sunshine)] fill-current flex-shrink-0" />
              <span className="font-bold flex-1 truncate">
                Yole favorite :{" "}
                {yoleById[favoriteYoleId]?.name || "?"}
              </span>
              {r.points_favorite > 0 ? (
                <span className="inline-flex items-center gap-1 text-emerald-700 font-mono font-bold">
                  <CheckCircle2 className="h-3.5 w-3.5" /> +{r.points_favorite}
                </span>
              ) : (
                <span className="text-[10px] font-mono text-slate-500">n'a pas gagné</span>
              )}
            </div>
          )}

          <Link
            to={`/etapes/${r.stage_id}`}
            data-testid={`my-ranking-goto-${r.id}`}
            className="inline-flex items-center gap-1 text-[11px] font-bold text-[color:var(--ocean)] hover:underline"
          >
            Voir l'étape →
          </Link>
        </div>
      )}
    </div>
  );
}
